import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * エラーバウンダリコンポーネント
 * アプリケーション内で発生したエラーをキャッチして適切に表示
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // エラー情報をコンソールに出力
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // エラー情報を状態に保存
    this.setState({
      error,
      errorInfo,
    });

    // 本番環境では、ここでエラーレポートサービスに送信
    if (import.meta.env.PROD) {
      // TODO: Sentryなどのエラートラッキングサービスに送信
      // reportErrorToService(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // カスタムフォールバックが指定されている場合はそれを表示
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // デフォルトのエラー表示
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <AlertCircle className="h-12 w-12 text-red-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  エラーが発生しました
                </h2>
                <p className="text-gray-600 mb-4">
                  申し訳ございません。予期しないエラーが発生しました。
                </p>
                
                {/* 開発環境ではエラー詳細を表示 */}
                {import.meta.env.DEV && this.state.error && (
                  <details className="mb-4">
                    <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                      エラー詳細を表示
                    </summary>
                    <div className="mt-2 p-3 bg-gray-100 rounded text-xs">
                      <pre className="whitespace-pre-wrap break-words text-red-600">
                        {this.state.error.toString()}
                      </pre>
                      {this.state.errorInfo && (
                        <pre className="mt-2 whitespace-pre-wrap break-words text-gray-600">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}

                <div className="flex space-x-3">
                  <Button
                    onClick={this.handleReset}
                    variant="primary"
                    size="sm"
                  >
                    もう一度試す
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/'}
                    variant="secondary"
                    size="sm"
                  >
                    ホームに戻る
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * 特定のエラーをスローするためのユーティリティ
 */
export function throwError(message: string): never {
  throw new Error(message);
}

/**
 * エラーをレポートするためのユーティリティ
 */
export function reportError(error: Error, context?: Record<string, any>) {
  console.error('Error reported:', error, context);
  
  // 本番環境では外部サービスに送信
  if (import.meta.env.PROD) {
    // TODO: エラーレポートサービスに送信
  }
}
